import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Link } from '@techstyle/next-routes';
import { HTMLAsset, ImageAsset } from '@techstyle/react-assets';

import { mobile, desktop } from '../../styles';
import { Component as AssetCTA } from '../AssetCTA';
import { Component as TemplateBackgroundImageAsset } from '../TemplateBackgroundImageAsset';
import TemplateButtons from '../TemplateButtons/TemplateButtons';

/**
 * By default, this Cell is a centered flex-column (within a flex Row). When there is a background image set, the flex-column needs to stretch to fill the full height and width of the container. `TemplateBackgroundImageAsset` takes care of center aligning the HTML text.
 */
const Cell = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  order: initial;
  padding: 0;

  text-align: center;

  ${props =>
    props.bgImage &&
    `
    align-items: stretch;
    align-self: stretch;
  `};

  ${desktop`
    flex: 0 0 50%;
    order: ${props => props.order || 'initial'};
  `};
`;

const TextCell = styled(Cell)`
  padding: 2em 1em;
  ${props => props.textStyles}
`;

const StyledImageAsset = styled(ImageAsset)`
  display: block;
  max-width: 100%;
`;

const OverCTAWrapper = styled.div`
  width: 100%;
  position: absolute;
  bottom: ${props =>
    props.position === 'bottom' ? `${props.positionSpace}px` : 'auto'};
  top: ${props =>
    props.position === 'top' ? `${props.positionSpace}px` : 'auto'};

  & > a {
    min-width: 365px;
  }

  ${mobile`
    bottom: ${props =>
      props.position === 'bottom' ? `${props.mobilePositionSpace}px` : 'auto'};
    top: ${props =>
      props.position === 'top' ? `${props.mobilePositionSpace}px` : 'auto'};

    & > a {
      min-width: 225px;
    }
  `}
`;

export default class TemplateImageAsset extends React.PureComponent {
  static propTypes = {
    asset: PropTypes.object.isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
    textStyles: PropTypes.array,
    isCtaOver: PropTypes.bool,
    link: PropTypes.string,
    trackButtonClick: PropTypes.func,
  };

  renderImage(asset) {
    const { options } = asset;
    const { link, onClick } = this.props;
    const urlLink = options.targetUrl || link;
    const onPressEnterKey = e => {
      if (e.code === 'Enter') {
        onClick(e);
      }
    };

    return urlLink ? (
      <Link route={urlLink}>
        <a
          onClick={onClick}
          onKeyPress={onPressEnterKey}
          role="link"
          tabIndex={0}
        >
          <StyledImageAsset asset={asset} />
        </a>
      </Link>
    ) : (
      <StyledImageAsset asset={asset} />
    );
  }

  renderText(order) {
    const { asset, className, textStyles, trackButtonClick, isCtaOver } =
      this.props;
    const hasSecondaryImage = asset.options.hoverImage;

    /**
     * If an hover image has been uploaded, use it as the background image
     */
    if (hasSecondaryImage) {
      return (
        <Cell bgImage order={order} className={className}>
          <TemplateBackgroundImageAsset asset={asset} hoverImage />
        </Cell>
      );
    }

    if (isCtaOver) {
      const { ctaVerticalSpace, ctaVerticalPosition, ctaVerticalSpaceMobile } =
        asset.options.customVars;
      return (
        <Cell bgImage order={order} className={className}>
          <TemplateBackgroundImageAsset asset={asset} hoverImage />
          <OverCTAWrapper
            position={ctaVerticalPosition}
            positionSpace={ctaVerticalSpace}
            mobilePositionSpace={ctaVerticalSpaceMobile}
          >
            <AssetCTA {...asset} />
          </OverCTAWrapper>
        </Cell>
      );
    }

    return (
      <TextCell order={order} className={className} textStyles={textStyles}>
        <HTMLAsset asset={asset} />
        <TemplateButtons asset={asset} trackButtonClick={trackButtonClick} />
      </TextCell>
    );
  }

  render() {
    const { asset, className } = this.props;
    const {
      options: {
        customVars: { imagePosition },
      },
    } = asset;

    return (
      <>
        <Cell order={imagePosition === 'left' ? 0 : 1} className={className}>
          {this.renderImage(asset)}
        </Cell>
        {this.renderText(imagePosition === 'left' ? 1 : 0)}
      </>
    );
  }
}
